import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAdminApi } from '../hooks/useAdminApi';
import {
  ArrowLeft, Truck, Container, Gauge, Save, User, Calendar, Loader2, Printer
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import SaaqInspectionClassic from '../../components/SaaqInspectionClassic';

const SaaqInspectionPage = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoprint = searchParams.get('autoprint');
  const { get: adminGet, post: adminPost } = useAdminApi();

  // Support atelier indépendant: fallback to mechanic_token
  const mechToken = typeof window !== 'undefined' ? localStorage.getItem('mechanic_token') : null;
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const isAtelier = !adminToken && !!mechToken;

  const get = useCallback(async (endpoint) => {
    const res = await adminGet(endpoint);
    if (res.success) return res;
    if (isAtelier) {
      // Retry with mechanic token
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
          headers: { 'Authorization': `Bearer ${mechToken}` }
        });
        const data = await r.json();
        return { success: r.ok, data: Array.isArray(data) ? data : data };
      } catch { return { success: false }; }
    }
    return res;
  }, [adminGet, isAtelier, mechToken]);

  const post = useCallback(async (endpoint, body) => {
    const res = await adminPost(endpoint, body);
    if (res.success) return res;
    if (isAtelier) {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${mechToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await r.json();
        return { success: r.ok, data };
      } catch { return { success: false }; }
    }
    return res;
  }, [adminPost, isAtelier, mechToken]);

  const [vehicle, setVehicle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [inspectorName, setInspectorName] = useState('');
  const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().split('T')[0]);
  const [odometer, setOdometer] = useState('');
  const [notes, setNotes] = useState('');
  const [itemStatuses, setItemStatuses] = useState({});
  const [measurements, setMeasurements] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    // Fetch all fleet vehicles (admin + atelier) and find ours
    const vehiclesRes = await get('/saas/fleet/vehicles');
    let foundVehicle = null;
    if (vehiclesRes.success) {
      foundVehicle = vehiclesRes.data.find(v => v.id === vehicleId);
    }

    // Also search atelier vehicles if not found in admin fleet
    if (!foundVehicle) {
      const atelierRes = await get('/saas/fleet/vehicles?include_atelier=true');
      if (atelierRes.success) {
        foundVehicle = atelierRes.data.find(v => v.id === vehicleId);
      }
    }

    if (!foundVehicle) {
      toast.error('Véhicule non trouvé');
      navigate(-1);
      return;
    }

    setVehicle(foundVehicle);
    setOdometer(foundVehicle.current_odometer?.toString() || '');

    // Fetch categories
    const catRes = await get(`/saas/saaq/categories?vehicle_type=${foundVehicle.vehicle_type}`);
    if (catRes.success) {
      setCategories(catRes.data.categories || []);
    }

    // Try to load latest inspection for this vehicle
    const inspRes = await get(`/saas/saaq/inspections?vehicle_id=${vehicleId}`);
    if (inspRes.success && inspRes.data?.length > 0) {
      const latest = inspRes.data[0]; // Already sorted by created_at desc
      if (latest.items?.length > 0) {
        const loadedStatuses = {};
        const loadedMeasurements = {};
        for (const item of latest.items) {
          const key = `${item.category_code}-${item.item_code}`;
          if (item.status) loadedStatuses[key] = item.status;
          if (item.measurement != null) loadedMeasurements[key] = item.measurement.toString();
        }
        setItemStatuses(loadedStatuses);
        setMeasurements(loadedMeasurements);
      }
      if (latest.inspector_name) setInspectorName(latest.inspector_name);
      if (latest.inspection_date) setInspectionDate(latest.inspection_date);
      if (latest.odometer) setOdometer(latest.odometer.toString());
      if (latest.notes) setNotes(latest.notes);
    }
    setLoading(false);
  }, [get, vehicleId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-print: navigate from fleet card with ?autoprint=classic triggers print
  useEffect(() => {
    if (autoprint !== 'classic' || loading) return;
    // Wait for classic form to render, then click its print button
    const timer = setTimeout(() => {
      const btn = document.querySelector('[data-testid="classic-print-btn"]');
      if (btn) btn.click();
    }, 600);
    return () => clearTimeout(timer);
  }, [autoprint, loading]);

  const handleItemStatusChange = (catCode, itemCode, status) => {
    const key = `${catCode}-${itemCode}`;
    setItemStatuses(prev => ({ ...prev, [key]: status }));
  };

  const handleMeasurementChange = (catCode, itemCode, value) => {
    const key = `${catCode}-${itemCode}`;
    setMeasurements(prev => ({ ...prev, [key]: value }));
  };

  const checkedCount = Object.keys(itemStatuses).length;
  const totalItems = categories.reduce((acc, c) => acc + c.items.length, 0);
  const majeurs = Object.values(itemStatuses).filter(s => s === 'Maj').length;
  const mineurs = Object.values(itemStatuses).filter(s => s === 'Min').length;
  const progress = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  const handleSubmit = async () => {
    if (!inspectorName.trim()) {
      toast.error("Nom de l'inspecteur requis");
      return;
    }

    if (checkedCount === 0) {
      toast.error("Veuillez vérifier au moins un élément");
      return;
    }

    setSubmitting(true);

    const items = [];
    categories.forEach(cat => {
      cat.items.forEach(item => {
        const key = `${cat.code}-${item.code}`;
        const status = itemStatuses[key];
        if (status) {
          const entry = {
            category_code: cat.code,
            category_name: cat.name,
            item_code: item.code,
            item_name: item.name,
            status,
            location: item.loc || 'C'
          };
          if (item.requires_measurement && measurements[key]) {
            entry.measurement = parseFloat(measurements[key]);
            entry.measurement_unit = item.measurement_unit;
          }
          items.push(entry);
        }
      });
    });

    const result = await post('/saas/saaq/inspection', {
      vehicle_id: vehicleId,
      inspector_name: inspectorName,
      inspection_date: inspectionDate,
      odometer: parseInt(odometer) || 0,
      items,
      notes
    });

    if (result.success) {
      const inspId = result.data?.inspection?.id;
      if (inspId) {
        // Auto-download PDF
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('admin_token') || localStorage.getItem('mechanic_token');
        try {
          const pdfResp = await fetch(`${apiUrl}/api/saas/saaq/inspections/${inspId}/pdf`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (pdfResp.ok) {
            const blob = await pdfResp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Inspection_Mecanique_${vehicle.unit_number}_${inspectionDate}.pdf`;
            const disposition = pdfResp.headers.get('Content-Disposition');
            if (disposition) {
              const match = disposition.match(/filename="(.+)"/);
              if (match) a.download = match[1];
            }
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        } catch { /* PDF download is bonus, don't block navigation */ }
      }
      toast.success('Inspection mécanique enregistrée — PDF téléchargé');
      navigate(-1);
    } else {
      toast.error(result.error || "Erreur lors de l'enregistrement");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-amber-500" size={32} />
      </div>
    );
  }

  if (!vehicle) return null;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Back */}
      <Link to="/admin/fleet" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors">
        <ArrowLeft size={18} />
        <span>Retour à la flotte</span>
      </Link>

      {/* Header */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center">
            {vehicle.vehicle_type === 'truck'
              ? <Truck className="text-amber-500" size={28} />
              : <Container className="text-blue-400" size={28} />}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="inspection-title">
              Inspection Mécanique — {vehicle.unit_number}
            </h1>
            <p className="text-slate-400 text-sm">
              {vehicle.brand} {vehicle.model} {vehicle.year}
              {vehicle.plate_number && ` · ${vehicle.plate_number}`}
              {vehicle.pnbv && ` · PNBV: ${vehicle.pnbv.toLocaleString()} kg`}
            </p>
          </div>
        </div>

        {/* Inspector info */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
              <User size={12} /> Inspecteur *
            </label>
            <Input
              value={inspectorName}
              onChange={e => setInspectorName(e.target.value)}
              placeholder="Nom de l'inspecteur"
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="input-inspector"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
              <Calendar size={12} /> Date
            </label>
            <Input
              type="date"
              value={inspectionDate}
              onChange={e => setInspectionDate(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="input-date"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
              <Gauge size={12} /> Odomètre (km)
            </label>
            <Input
              type="number"
              value={odometer}
              onChange={e => setOdometer(e.target.value)}
              placeholder="0"
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="input-odometer"
            />
          </div>
          <div className="flex items-end">
            <div className="w-full bg-slate-800 rounded-lg p-2.5 text-center">
              <p className="text-xs text-slate-500">Progression</p>
              <p className="text-lg font-bold text-amber-500">{progress}%</p>
              <p className="text-xs text-slate-500">{checkedCount}/{totalItems}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Inspection Form */}
      <SaaqInspectionClassic
          categories={categories}
          itemStatuses={itemStatuses}
          onItemStatusChange={handleItemStatusChange}
          measurements={measurements}
          onMeasurementChange={handleMeasurementChange}
          vehicleType={vehicle?.vehicle_type || 'truck'}
          vehicleInfo={vehicle || {}}
          inspectorName={inspectorName}
          inspectionDate={inspectionDate}
          odometer={odometer}
          notes={notes}
          onNotesChange={setNotes}
        />

      {/* Submit */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 sticky bottom-4">
        <div className="text-sm text-slate-400">
          {checkedCount === 0 ? (
            <span>Commencez à vérifier les éléments</span>
          ) : (
            <span>
              <strong className="text-white">{checkedCount}</strong>/{totalItems} éléments vérifiés
              {majeurs > 0 && <span className="text-red-400 ml-2">· {majeurs} majeure(s)</span>}
              {mineurs > 0 && <span className="text-amber-400 ml-2">· {mineurs} mineure(s)</span>}
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-slate-700 text-slate-300"
          >
            Annuler
          </Button>
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="print-btn border-slate-700 text-slate-300"
            data-testid="print-inspection-btn"
          >
            <Printer size={16} className="mr-2" />
            Imprimer
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || checkedCount === 0}
            className="bg-amber-500 hover:bg-amber-600 text-black min-w-[180px]"
            data-testid="submit-inspection-btn"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin mr-2" />
            ) : (
              <Save size={16} className="mr-2" />
            )}
            Enregistrer l'inspection
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaaqInspectionPage;
